import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Trash2, Send, PenLine, ImagePlus, X, BadgeCheck, ShieldCheck, Globe, Star, ThumbsUp, Bookmark, Flag, Share2, Archive, Zap, MapPin, UserPlus, UserCheck, Search, Sparkles, BellPlus, BellRing, CheckCircle2, CornerDownRight, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Spinner } from "./common";
import LinkPreviews from "./LinkPreviewCard";

function timeAgo(iso) {
  try {
    const d = new Date(iso), s = (Date.now() - d.getTime()) / 1000;
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

const URL_RE = /(https?:\/\/[^\s]+)/g;

/* Post tags — primary tag + up to 6 secondary free-form tags per post */
export const POST_TAGS = [
  ["comment", "💬", "Comment"], ["question", "❓", "Question"], ["travel_tip", "✈️", "Travel Tip"],
  ["food_tip", "🍴", "Food Tip"], ["suggestion", "💡", "Suggestion"], ["complaint", "⚠️", "Complaint"],
  ["warning", "🚨", "Warning / Alert"], ["flight_delay", "⏰", "Flight Delay"], ["gate_change", "🚪", "Gate Change"],
  ["flight_cancellation", "❌", "Cancellation"], ["baggage", "🧳", "Baggage"], ["immigration_visa", "🛂", "Immigration / Visa"],
  ["security", "🔐", "Security"], ["lounge", "🛋️", "Lounge"], ["ground_transport", "🚗", "Ground Transport"],
  ["hotel_stay", "🏨", "Hotel / Stay"], ["deal", "💰", "Deal / Saving"], ["photo", "📷", "Photo"],
  ["useful_link", "🔗", "Useful Link"], ["news", "📢", "News / Update"], ["local_info", "📍", "Local Info"],
  ["official_response", "🛡️", "Official Response"], ["other", "➕", "Other"],
];
const TAG_META = Object.fromEntries(POST_TAGS.map(([v, e, l]) => [v, { e, l }]));
const FLIGHT_TAGS = ["flight_delay", "gate_change", "flight_cancellation", "baggage"];
const FILTER_CHIPS = [["", "All"], ["question", "Questions"], ["travel_tip", "Tips"], ["complaint", "Complaints"],
  ["warning", "Alerts"], ["flight_delay", "Delays"], ["gate_change", "Gate Changes"], ["food_tip", "Food"],
  ["photo", "Photos"], ["official", "Official"]];
const EXPIRY_OPTS = [["", "How long is this useful?"], ["live", "Live / Now (1h)"], ["today", "Today"],
  ["until_departure", "Until flight departure"], ["24h", "24 hours"], ["7d", "7 days"], ["none", "No expiration"]];

/* TravelerPulse original reaction set — one active reaction per user per post. */
export const REACTIONS = [
  ["like", "👍", "Like"], ["love", "❤️", "Love"], ["wow", "😮", "Wow"],
  ["thanks", "🤗", "Thanks"], ["helpful", "💡", "Helpful"], ["great_tip", "✈️", "Great Tip"],
];
const REACT_META = Object.fromEntries(REACTIONS.map(([v, e, l]) => [v, { e, l }]));

export const TagPill = ({ tag, other }) => {
  const m = TAG_META[tag] || TAG_META.comment;
  return <span className="inline-flex items-center gap-1 bg-fuchsia-50 text-fuchsia-800 border border-fuchsia-200 text-[11px] font-700 px-1.5 py-0.5 rounded notranslate" data-testid="post-tag-pill">{m.e} {tag === "other" && other ? other : m.l}</span>;
};

/* Languages travellers can write posts/replies in. The page-level "Translate"
   dropdown (Google) then translates any post to the reader's language. */
export const INPUT_LANGS = ["English", "Español", "Français", "Deutsch", "Italiano", "Português",
  "Nederlands", "Polski", "Русский", "Türkçe", "العربية", "हिन्दी", "தமிழ்", "తెలుగు", "മലയാളം",
  "اردو", "中文", "日本語", "한국어", "ไทย", "Tiếng Việt", "Bahasa Indonesia"];

export const LangSelect = ({ value, onChange, compact, testId }) => (
  <label className={`inline-flex items-center gap-1 text-muted-foreground cursor-pointer notranslate ${compact ? "text-xs" : "text-sm"}`}>
    <Globe className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      aria-label="Writing language"
      className="bg-transparent border border-zinc-300 px-1 py-0.5 text-xs outline-none focus:ring-1 focus:ring-primary cursor-pointer max-w-[110px]">
      {INPUT_LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
    </select>
  </label>
);

export const SupporterBadge = ({ badge }) => (
  badge
    ? <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-[11px] font-600 px-1.5 py-0.5 rounded notranslate" data-testid="supporter-badge" title={`Founding Supporter — ${badge}`}>
        <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> {badge}
      </span>
    : null
);

export const LangBadge = ({ language }) => (
  language && language !== "English"
    ? <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-[11px] font-600 px-1.5 py-0.5 rounded notranslate" data-testid="post-lang-badge">
        <Globe className="w-3 h-3" /> {language}
      </span>
    : null
);

export function Linkify({ text }) {
  if (!text) return null;
  return text.split(URL_RE).map((part, i) =>
    URL_RE.test(part)
      ? <a key={i} href={part} target="_blank" rel="noreferrer nofollow" className="text-primary hover:underline break-all">{part}</a>
      : <span key={i}>{part}</span>
  );
}

const Avatar = ({ name, picture }) => (
  picture
    ? <img src={picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
    : <div className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center font-mono text-sm shrink-0">{(name || "U")[0].toUpperCase()}</div>
);

export const OfficialBadge = ({ label }) => (
  <span className="inline-flex items-center gap-1 bg-sky-100 text-sky-700 text-xs font-700 px-1.5 py-0.5 rounded" data-testid="official-badge">
    <BadgeCheck className="w-3.5 h-3.5" /> {label || "Official"}
  </span>
);

export default function DiscussionBoard({ entityType, entityId, entityName, kind = "comment", title, placeholder }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [ttl, setTtl] = useState("");
  const [posting, setPosting] = useState(false);
  const [image, setImage] = useState(null);
  const [asOfficial, setAsOfficial] = useState(false);
  const [canOfficial, setCanOfficial] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem("tp_write_lang") || "English");
  const [tag, setTag] = useState("comment");
  const [tagOther, setTagOther] = useState("");
  const [secTags, setSecTags] = useState(entityName || "");
  const [expiry, setExpiry] = useState("");
  const [flight, setFlight] = useState({});
  const [filterTag, setFilterTag] = useState("");
  const [sort, setSort] = useState("newest");
  const [showArchive, setShowArchive] = useState(false);
  const [followed, setFollowed] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [stats, setStats] = useState(null); // {count, followers, following}
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [similar, setSimilar] = useState([]);
  const textRef = useRef(null);
  const isStory = kind === "story";

  const load = useCallback(() => {
    const params = { entity_type: entityType, entity_id: entityId, kind };
    if (filterTag === "official") params.sort = "official";
    else if (filterTag) params.tag = filterTag;
    if (sort !== "newest" && filterTag !== "official") params.sort = sort;
    if (search.trim()) params.search = search.trim();
    if (sort === "nearby") {
      try {
        const loc = JSON.parse(localStorage.getItem("aa_location") || "null");
        const near = [loc?.city, loc?.state, loc?.country].filter(Boolean).join(",");
        if (near) params.near = near;
      } catch { /* no location saved */ }
    }
    if (showArchive) params.show = "archive";
    return api.get("/community/posts", { params }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [entityType, entityId, kind, filterTag, sort, showArchive, search]);
  useEffect(() => { setItems(null); load(); }, [load]);

  // Discussion stats — post count + follower count + am-I-following (Phase A/C)
  const loadStats = useCallback(() => {
    if (isStory) return;
    api.get("/community/count", { params: { entity_type: entityType, entity_id: entityId } })
      .then((r) => setStats(r.data)).catch(() => {});
  }, [entityType, entityId, isStory]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const followEntity = async () => {
    if (!user) { toast.error("Sign in to follow this discussion"); return; }
    try {
      const r = await api.post("/community/follow-entity", { entity_type: entityType, entity_id: entityId, entity_name: entityName });
      setStats((s) => s ? { ...s, following: r.data.following, followers: Math.max(0, s.followers + (r.data.following ? 1 : -1)) } : s);
      toast.success(r.data.following ? "Following — you'll get a bell notification for new posts here" : "Unfollowed this discussion");
    } catch { toast.error("Could not update follow"); }
  };

  // "Ask the Community" / "Share a Tip" buttons inside articles open this composer pre-set
  useEffect(() => {
    if (isStory) return undefined;
    const h = (e) => {
      const t = e.detail?.tag;
      if (t) setTag(t);
      setTimeout(() => textRef.current?.focus(), 450);
    };
    window.addEventListener("tp-open-composer", h);
    return () => window.removeEventListener("tp-open-composer", h);
  }, [isStory]);

  // Duplicate-question detection (Phase D) — debounce while typing a Question
  useEffect(() => {
    if (isStory || tag !== "question" || text.trim().length < 15) { setSimilar([]); return undefined; }
    const t = setTimeout(() => {
      api.get("/community/similar", { params: { entity_type: entityType, entity_id: entityId, q: text.slice(0, 200) } })
        .then((r) => setSimilar(r.data.items || [])).catch(() => {});
    }, 700);
    return () => clearTimeout(t);
  }, [tag, text, entityType, entityId, isStory]);

  const loadSummary = async () => {
    if (summary) { setSummary(null); return; }
    setSummaryLoading(true);
    try {
      const r = await api.get("/community/summary", { params: { entity_type: entityType, entity_id: entityId } });
      if (r.data.summary) setSummary(r.data.summary);
      else toast.info("Not enough recent posts to summarize yet");
    } catch { toast.error("Could not load summary"); }
    finally { setSummaryLoading(false); }
  };

  useEffect(() => {
    if (!user) { setFollowed(new Set()); return; }
    api.get("/community/follow/ids").then((r) => setFollowed(new Set(r.data.ids))).catch(() => {});
  }, [user]);

  const toggleFollow = async (targetUid) => {
    if (!user) { toast.error("Sign in to follow travellers"); return; }
    try {
      const r = await api.post(`/community/follow/${targetUid}`);
      setFollowed((prev) => {
        const next = new Set(prev);
        if (r.data.following) next.add(targetUid); else next.delete(targetUid);
        return next;
      });
      toast.success(r.data.following ? "Following — their posts now appear in your Following feed" : "Unfollowed");
      if (sort === "following") load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update follow"); }
  };

  useEffect(() => {
    if (!user) { setCanOfficial(false); return; }
    if (["moderator", "admin"].includes(user.role)) { setCanOfficial(true); return; }
    api.get("/officials/me").then((r) => {
      setCanOfficial((r.data.affiliations || []).some((a) => a.entity_type === entityType && String(a.entity_id) === String(entityId)));
    }).catch(() => setCanOfficial(false));
  }, [user, entityType, entityId]);

  const submit = async (confirmSensitive = false) => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const fd = new FormData();
      fd.append("text", text); fd.append("entity_type", entityType); fd.append("entity_id", entityId);
      fd.append("kind", kind); fd.append("title", ttl);
      fd.append("language", lang);
      if (confirmSensitive) fd.append("confirm_sensitive", "true");
      if (entityName) fd.append("entity_name", entityName);
      if (asOfficial && canOfficial) fd.append("as_official", "true");
      if (!isStory) {
        fd.append("tag", asOfficial && canOfficial && tag === "comment" ? "official_response" : tag);
        if (tag === "other" && tagOther) fd.append("tag_other", tagOther);
        if (secTags.trim()) fd.append("tags_secondary", secTags);
        if (expiry) fd.append("expires_choice", expiry);
        if (FLIGHT_TAGS.includes(tag) && Object.values(flight).some(Boolean)) fd.append("flight_json", JSON.stringify(flight));
      }
      if (image) fd.append("file", image);
      const r = await api.post("/community/posts", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText(""); setTtl(""); setImage(null); setAsOfficial(false);
      setTag("comment"); setTagOther(""); setSecTags(entityName || ""); setExpiry(""); setFlight({}); setSimilar([]);
      await load();
      loadStats();
      if (r.data?.warning) toast.warning(r.data.warning);
      else toast.success(isStory ? "Story shared" : "Posted");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 422 && d?.code === "sensitive" && !confirmSensitive) {
        setPosting(false);
        const msg = `${(d.warnings || []).join("\n")}\n\nSharing private details publicly can be risky. Post anyway?`;
        if (window.confirm(msg)) return submit(true);
        return;
      }
      toast.error(typeof d === "string" ? d : "Could not post");
    } finally { setPosting(false); }
  };

  const react = async (id, kindR) => {
    if (!user) { toast.error("Sign in to react"); return; }
    setItems((prev) => prev.map((p) => {
      if (p.id !== id) return p;
      const rx = { ...(p.reactions || {}) };
      const prevK = p.my_reaction;
      if (prevK) rx[prevK] = Math.max(0, (rx[prevK] || 1) - 1);
      const off = prevK === kindR;
      if (!off) rx[kindR] = (rx[kindR] || 0) + 1;
      const total = Object.values(rx).reduce((a, b) => a + b, 0);
      return { ...p, reactions: rx, my_reaction: off ? null : kindR, reaction_total: total };
    }));
    try { await api.post(`/community/posts/${id}/react`, { kind: kindR }); } catch { load(); }
  };

  const solve = async (postId, commentId) => {
    try {
      await api.post(`/community/posts/${postId}/solve`, { comment_id: commentId });
      toast.success(commentId ? "Marked as the helpful answer" : "Answer unmarked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark answer"); }
  };
  const save = async (id) => {
    if (!user) { toast.error("Sign in to save"); return; }
    setItems((prev) => prev.map((p) => p.id === id ? { ...p, saved_by_me: !p.saved_by_me } : p));
    try { const r = await api.post(`/community/posts/${id}/save`); toast.success(r.data.saved ? "Saved" : "Removed from saved"); } catch { load(); }
  };
  const report = async (id) => {
    if (!user) { toast.error("Sign in to report"); return; }
    const reason = window.prompt("Why are you reporting this post? (optional)") ?? null;
    if (reason === null) return;
    try { await api.post(`/community/posts/${id}/report`, { reason }); toast.success("Reported — our moderators will review it"); } catch { toast.error("Could not report"); }
  };

  const del = async (id) => {
    try { await api.delete(`/community/posts/${id}`); setItems((p) => p.filter((x) => x.id !== id)); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div data-testid={`board-${kind}`}>
      {!isStory ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-5">
          <h2 data-testid="board-title" className="font-heading font-900 text-fuchsia-600 text-2xl flex items-center gap-2 notranslate">
            <MessageCircle className="w-6 h-6" /> {(entityName || title || "Community").replace(/ (traveler forum|discussion board)$/i, "")} Talk – Vibes
          </h2>
          {stats && (
            <span data-testid="board-post-count" className="text-xs font-mono text-muted-foreground">
              {stats.count} post{stats.count === 1 ? "" : "s"} · {stats.followers} following
            </span>
          )}
          <button onClick={followEntity} data-testid="board-follow-discussion"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-700 border rounded-full transition-colors ${stats?.following ? "bg-fuchsia-600 text-white border-fuchsia-600" : "border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"}`}>
            {stats?.following ? <><BellRing className="w-3.5 h-3.5" /> Following</> : <><BellPlus className="w-3.5 h-3.5" /> Follow discussion</>}
          </button>
          {stats?.count >= 5 && (
            <button onClick={loadSummary} disabled={summaryLoading} data-testid="board-summary-toggle"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-700 border border-indigo-300 text-indigo-700 rounded-full hover:bg-indigo-50 transition-colors disabled:opacity-50">
              <Sparkles className="w-3.5 h-3.5" /> {summaryLoading ? "Summarizing…" : summary ? "Hide summary" : "What travelers are discussing"}
            </button>
          )}
        </div>
      ) : (title && <h2 className="font-heading font-900 text-fuchsia-600 text-2xl mb-5 flex items-center gap-2"><MessageCircle className="w-6 h-6" /> {title}</h2>)}

      {summary && !isStory && (
        <div className="border border-indigo-200 bg-indigo-50/50 p-4 mb-5 text-sm whitespace-pre-line" data-testid="board-summary">
          <div className="font-700 text-indigo-800 mb-1 flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> What travelers are discussing</div>
          {summary}
          <div className="text-[10px] text-muted-foreground mt-2">AI summary of recent community posts — traveler reports, not verified information.</div>
        </div>
      )}

      {user && !canOfficial && (entityType === "airline" || entityType === "airport") && (
        <Link to={`/officials/apply?type=${entityType}&id=${entityId}&name=${encodeURIComponent(entityName || "")}`}
          data-testid="board-represent-link" className="inline-flex items-center gap-1.5 text-sm text-sky-700 hover:underline mb-4">
          <ShieldCheck className="w-4 h-4" /> Work here? Apply to respond as an official
        </Link>
      )}

      {user ? (
        <div className="border border-zinc-300 bg-white p-4 mb-6">
          <input value={ttl} onChange={(e) => setTtl(e.target.value)} data-testid="board-title-input"
            placeholder={isStory ? "Story title (e.g. My First Trip to India — 2008)" : "Title (optional — e.g. Gate changed for EK202, Great sunset spot in Fort Kochi)"}
            className="w-full border border-zinc-300 px-3 py-2 mb-2 outline-none focus:ring-2 focus:ring-primary text-base" />
          <textarea ref={textRef} value={text} onChange={(e) => setText(e.target.value)} data-testid="board-text-input" rows={isStory ? 4 : 2} dir="auto"
            placeholder={placeholder || (isStory ? "Share your travel story…" : "Start a discussion, add a link, or ask a question…")}
            className="w-full border border-zinc-300 px-3 py-2 outline-none focus:ring-2 focus:ring-primary text-base resize-y" />

          {similar.length > 0 && (
            <div className="border border-amber-300 bg-amber-50 p-3 mt-2 text-sm" data-testid="board-similar">
              <div className="font-700 text-amber-800 mb-1">Similar discussions already exist:</div>
              {similar.map((s) => (
                <Link key={s.id} to={`/community/post/${s.id}`} target="_blank" data-testid={`similar-${s.id}`}
                  className="block text-primary hover:underline py-0.5 truncate">→ {s.title}</Link>
              ))}
              <div className="text-[11px] text-muted-foreground mt-1">You can check these first — or post anyway below.</div>
            </div>
          )}

          {!isStory && (
            <>
              <div className="flex gap-1.5 overflow-x-auto py-2 -mx-1 px-1" data-testid="board-tag-picker">
                {POST_TAGS.filter(([v]) => v !== "official_response" || canOfficial).map(([v, e, l]) => (
                  <button key={v} type="button" onClick={() => setTag(v)} data-testid={`board-tag-${v}`}
                    className={`shrink-0 px-2 py-1 text-xs font-600 border rounded-full transition-colors ${tag === v ? "bg-fuchsia-600 text-white border-fuchsia-600" : "border-zinc-300 text-zinc-700 hover:border-fuchsia-400"}`}>
                    {e} {l}
                  </button>
                ))}
              </div>
              {tag === "other" && (
                <input value={tagOther} onChange={(e) => setTagOther(e.target.value)} placeholder="Describe your post type" data-testid="board-tag-other"
                  className="w-full border border-zinc-300 px-3 py-1.5 mb-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
              )}
              {FLIGHT_TAGS.includes(tag) && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mb-2 border border-fuchsia-200 bg-fuchsia-50/40 p-2" data-testid="board-flight-fields">
                  {[["airline", "Airline"], ["flight_number", "Flight # (e.g. AS123)"], ["flight_date", "date"], ["dep", "From (PDX)"], ["arr", "To (SEA)"], ["sched_time", "Scheduled time"]].map(([k, ph]) => (
                    k === "flight_date"
                      ? <input key={k} type="date" value={flight[k] || ""} onChange={(e) => setFlight({ ...flight, [k]: e.target.value })} data-testid={`board-flight-${k}`} className="border border-zinc-300 px-2 py-1 text-xs" />
                      : <input key={k} value={flight[k] || ""} onChange={(e) => setFlight({ ...flight, [k]: e.target.value })} placeholder={ph} data-testid={`board-flight-${k}`} className="border border-zinc-300 px-2 py-1 text-xs" />
                  ))}
                  {tag === "gate_change" && [["prev_gate", "Old gate (C8)"], ["new_gate", "New gate (C12)"], ["terminal", "Terminal"], ["time_reported", "Time reported"]].map(([k, ph]) => (
                    <input key={k} value={flight[k] || ""} onChange={(e) => setFlight({ ...flight, [k]: e.target.value })} placeholder={ph} data-testid={`board-flight-${k}`} className="border border-zinc-300 px-2 py-1 text-xs" />
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5 mb-1">
                <input value={secTags} onChange={(e) => setSecTags(e.target.value)} data-testid="board-sec-tags"
                  placeholder="Extra tags, comma separated (e.g. United Airlines, UA123, PDX)"
                  className="flex-1 min-w-[200px] border border-zinc-300 px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-primary" />
                <select value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="board-expiry"
                  className="border border-zinc-300 px-2 py-1.5 text-xs bg-white">
                  {EXPIRY_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </>
          )}

          {image && (
            <div className="mt-2 relative inline-block">
              <img src={URL.createObjectURL(image)} alt="preview" className="h-24 rounded border border-zinc-200 object-cover" />
              <button onClick={() => setImage(null)} className="absolute -top-2 -right-2 bg-rose-600 text-white rounded-full p-0.5"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-2">
            <LangSelect value={lang} onChange={(v) => { setLang(v); localStorage.setItem("tp_write_lang", v); }} testId="board-lang-select" />
            <label className="inline-flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer hover:text-primary" data-testid="board-image-label">
              <ImagePlus className="w-4 h-4" /> Photo
              <input type="file" accept="image/*" className="hidden" data-testid="board-image-input"
                onChange={(e) => setImage(e.target.files?.[0] || null)} />
            </label>
            {canOfficial && (
              <label className="inline-flex items-center gap-1.5 text-sm text-sky-700 cursor-pointer" data-testid="board-official-toggle">
                <input type="checkbox" checked={asOfficial} onChange={(e) => setAsOfficial(e.target.checked)} />
                <ShieldCheck className="w-4 h-4" /> Post as official
              </label>
            )}
            <button onClick={submit} disabled={posting || !text.trim()} data-testid="board-submit"
              className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-600 disabled:opacity-50 hover:bg-blue-700 transition-colors">
              {isStory ? <PenLine className="w-4 h-4" /> : <Send className="w-4 h-4" />}{isStory ? "Share story" : "Post"}
            </button>
          </div>
        </div>
      ) : (
        <div className="border border-zinc-300 bg-zinc-50 p-4 mb-6 text-sm text-muted-foreground">
          <Link to="/login" className="text-primary font-600 hover:underline">Sign in</Link> to {isStory ? "share your travel story" : "join the discussion"}.
        </div>
      )}

      {!isStory && (
        <div className="mb-4" data-testid="board-filters">
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {FILTER_CHIPS.map(([v, l]) => (
              <button key={v} onClick={() => setFilterTag(v)} data-testid={`board-filter-${v || "all"}`}
                className={`shrink-0 px-2.5 py-1 text-xs font-700 border rounded-full transition-colors ${filterTag === v ? "bg-primary text-white border-primary" : "border-zinc-300 text-zinc-700 hover:border-primary"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2 text-xs flex-wrap">
            {[["newest", "Newest"], ["helpful", "Most Helpful"], ["live", "Live Now"], ["nearby", "Nearby"], ["following", "Following"]].map(([v, l]) => (
              <button key={v} data-testid={`board-sort-${v}`}
                onClick={() => {
                  if (v === "following" && !user) { toast.error("Sign in to see posts from travellers you follow"); return; }
                  if (v === "nearby" && !localStorage.getItem("aa_location")) { toast.error("Set your location in the top bar to see nearby posts"); return; }
                  setSort(v); setShowArchive(false);
                }}
                className={`inline-flex items-center gap-1 px-2 py-1 border ${sort === v && !showArchive ? "bg-[#EA4335] text-white border-[#EA4335]" : "border-zinc-300 text-zinc-600 hover:border-zinc-500"}`}>
                {v === "live" && <Zap className="w-3 h-3" />}{v === "nearby" && <MapPin className="w-3 h-3" />}{v === "following" && <UserCheck className="w-3 h-3" />}{l}
              </button>
            ))}
            <button onClick={() => setShowArchive((a) => !a)} data-testid="board-archive-toggle"
              className={`inline-flex items-center gap-1 px-2 py-1 border ml-auto ${showArchive ? "bg-[#EA4335] text-white border-[#EA4335]" : "border-zinc-300 text-zinc-600 hover:border-zinc-500"}`}>
              <Archive className="w-3 h-3" /> Archive
            </button>
          </div>
          <div className="flex items-center gap-1.5 mt-2" data-testid="board-search-row">
            <div className="relative flex-1 max-w-xs">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput)}
                data-testid="board-search-input" placeholder="Search posts, tips, flight numbers…"
                className="w-full border border-zinc-300 pl-7 pr-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-primary" />
            </div>
            <button onClick={() => setSearch(searchInput)} data-testid="board-search-btn"
              className="px-2.5 py-1.5 text-xs font-700 border border-zinc-300 hover:border-primary">Search</button>
            {search && (
              <button onClick={() => { setSearch(""); setSearchInput(""); }} data-testid="board-search-clear"
                className="px-2 py-1.5 text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1"><X className="w-3 h-3" /> Clear</button>
            )}
          </div>
        </div>
      )}

      {!items ? <Spinner /> : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{isStory ? "No stories yet — be the first to share." : showArchive ? "No archived posts here yet." : sort === "following" ? "No posts from travellers you follow yet — tap Follow on any post to build your feed." : sort === "nearby" ? "No nearby posts yet — posts mentioning your city, region or country will appear here." : filterTag ? "No posts with this tag yet — be the first." : "No posts yet — start the conversation."}</p>
      ) : (
        <div className="space-y-4">
          {items.map((p) => <PostCard key={p.id} p={p} user={user} canOfficial={canOfficial} followed={followed} onFollow={toggleFollow} onReact={react} onSave={save} onReport={report} onDelete={del} onReload={load} onSolve={solve} isStory={isStory} />)}
        </div>
      )}
    </div>
  );
}

function PostCard({ p, user, canOfficial, followed, onFollow, onReact, onSave, onReport, onDelete, onReload, onSolve, isStory }) {
  const [reply, setReply] = useState("");
  const [open, setOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // root comment id being replied to (null = top level)
  const [replyOfficial, setReplyOfficial] = useState(false);
  const [replyLang, setReplyLang] = useState(() => localStorage.getItem("tp_write_lang") || "English");
  const [showPicker, setShowPicker] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const addComment = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/community/posts/${p.id}/comments`,
        { text: reply, parent_id: replyTo || undefined, as_official: replyOfficial && canOfficial, language: replyLang });
      setReply(""); setReplyTo(null); setReplyOfficial(false); onReload();
    } catch { toast.error("Could not comment"); }
  };

  const permalink = `${window.location.origin}/community/post/${p.id}`;
  const shareTitle = p.title || (p.text || "").slice(0, 80);
  const copyLink = () => {
    try { navigator.clipboard?.writeText(permalink)?.catch(() => {}); } catch (_) { /* ignore */ }
    toast.success("Link copied to clipboard");
  };

  const roots = (p.comments || []).filter((c) => !c.parent_id);
  const children = {};
  (p.comments || []).forEach((c) => { if (c.parent_id) (children[c.parent_id] = children[c.parent_id] || []).push(c); });
  const myReact = p.my_reaction ? REACT_META[p.my_reaction] : null;
  const topReacts = Object.entries(p.reactions || {}).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]).slice(0, 3);
  const isQuestion = p.tag === "question";
  const canSolve = user && (user.user_id === p.user_id || ["moderator", "admin"].includes(user.role));

  const CommentRow = ({ c, isChild }) => (
    <div className={`flex items-start gap-2 ${isChild ? "ml-8" : ""} ${c.is_official ? "bg-sky-50/50 -ml-3 pl-3 py-1 rounded" : ""} ${p.solved_comment_id === c.id ? "bg-emerald-50 border border-emerald-200 -ml-3 pl-3 py-1.5 pr-2 rounded" : ""}`} data-testid={`comment-${c.id}`}>
      {isChild && <CornerDownRight className="w-3.5 h-3.5 text-zinc-400 mt-1 shrink-0" />}
      <Avatar name={c.user_name} picture={c.user_picture} />
      <div className="text-sm min-w-0 flex-1">
        <span className="font-700">{c.user_name || "Traveller"}</span>
        {c.is_official && <span className="ml-1"><OfficialBadge label={c.official_label} /></span>}
        {c.supporter_badge && <span className="ml-1"><SupporterBadge badge={c.supporter_badge} /></span>}
        {c.language && c.language !== "English" && <span className="ml-1"><LangBadge language={c.language} /></span>}
        <span className="text-muted-foreground"> · {timeAgo(c.created_at)}</span>
        {p.solved_comment_id === c.id && (
          <span className="ml-1.5 inline-flex items-center gap-1 text-[11px] font-700 text-emerald-700" data-testid={`comment-solved-${c.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Helpful Answer
          </span>
        )}
        <p className="text-zinc-800" dir="auto"><Linkify text={c.text} /></p>
        <div className="flex items-center gap-3 mt-0.5">
          {!isChild && user && (
            <button onClick={() => { setReplyTo(replyTo === c.id ? null : c.id); setOpen(true); }} data-testid={`comment-reply-${c.id}`}
              className={`text-[11px] font-700 ${replyTo === c.id ? "text-fuchsia-700" : "text-muted-foreground hover:text-primary"}`}>
              {replyTo === c.id ? "Replying…" : "Reply"}
            </button>
          )}
          {isQuestion && canSolve && p.solved_comment_id !== c.id && (
            <button onClick={() => onSolve(p.id, c.id)} data-testid={`comment-mark-answer-${c.id}`}
              className="text-[11px] font-700 text-emerald-700 hover:underline inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Mark as answer
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div id={`post-${p.id}`} className={`border bg-white p-4 ${p.is_official ? "border-sky-300 bg-sky-50/30" : "border-zinc-300"}`} data-testid={`post-${p.id}`}>
      <div className="flex items-start gap-3">
        <Avatar name={p.user_name} picture={p.user_picture} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span className="font-700">{p.user_name || "Traveller"}</span>
            {user && p.user_id && p.user_id !== user.user_id && (
              <button onClick={() => onFollow(p.user_id)} data-testid={`post-follow-${p.id}`}
                className={`inline-flex items-center gap-1 text-[11px] font-700 px-1.5 py-0.5 rounded border transition-colors ${followed?.has(p.user_id) ? "bg-indigo-600 text-white border-indigo-600" : "border-indigo-300 text-indigo-700 hover:bg-indigo-50"}`}>
                {followed?.has(p.user_id) ? <><UserCheck className="w-3 h-3" /> Following</> : <><UserPlus className="w-3 h-3" /> Follow</>}
              </button>
            )}
            {p.is_official && <OfficialBadge label={p.official_label} />}
            <SupporterBadge badge={p.supporter_badge} />
            <LangBadge language={p.language} />
            <span className="text-muted-foreground">· {timeAgo(p.created_at)}</span>
            {(user && (user.user_id === p.user_id || ["moderator", "admin"].includes(user.role))) && (
              <button onClick={() => onDelete(p.id)} data-testid={`post-delete-${p.id}`} className="ml-auto text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
          {p.title && <div className="font-heading font-900 text-lg mt-1" data-testid={`post-title-${p.id}`}>{p.title}</div>}
          {!isStory && (
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              {p.tag && p.tag !== "comment" && <TagPill tag={p.tag} other={p.tag_other} />}
              {isQuestion && p.solved_comment_id && (
                <span className="inline-flex items-center gap-1 text-[11px] font-700 bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded" data-testid={`post-solved-${p.id}`}>
                  <CheckCircle2 className="w-3 h-3" /> Solved
                </span>
              )}
              {(p.tags_secondary || []).map((t) => <span key={t} className="text-[11px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded notranslate" data-testid="post-sec-tag">{t}</span>)}
              {p.expired && <span className="inline-flex items-center gap-1 text-[11px] font-700 bg-zinc-200 text-zinc-600 px-1.5 py-0.5 rounded" data-testid={`post-archived-${p.id}`}><Archive className="w-3 h-3" /> Archived</span>}
              {!p.expired && p.expires_at && <span className="inline-flex items-center gap-1 text-[11px] font-700 bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded" data-testid={`post-live-${p.id}`}><Zap className="w-3 h-3" /> Live</span>}
            </div>
          )}
          {p.flight && (
            <div className="mt-1.5 border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-xs font-mono" data-testid={`post-flight-${p.id}`}>
              {[p.flight.airline, p.flight.flight_number, p.flight.flight_date].filter(Boolean).join(" · ")}
              {(p.flight.dep || p.flight.arr) && <> — {p.flight.dep || "?"} → {p.flight.arr || "?"}</>}
              {p.flight.prev_gate && p.flight.new_gate && <> · Gate {p.flight.prev_gate} → {p.flight.new_gate}</>}
              {p.flight.terminal && <> · Terminal {p.flight.terminal}</>}
              {p.flight.sched_time && <> · {p.flight.sched_time}</>}
              {p.flight.time_reported && <> · reported {p.flight.time_reported}</>}
            </div>
          )}
          <p className="text-base text-zinc-800 mt-1 whitespace-pre-line" dir="auto"><Linkify text={p.text} /></p>
          <LinkPreviews text={p.text} />

          {p.image_url && (
            <img src={p.image_url} alt="" className="mt-3 rounded border border-zinc-200 max-h-96 object-cover" data-testid={`post-image-${p.id}`} />
          )}
          {p.image_status === "pending" && <div className="text-xs text-amber-600 mt-1">Image awaiting review</div>}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-sm">
            <div className="relative">
              <button onClick={() => { if (!user) { toast.error("Sign in to react"); return; } setShowPicker((v) => !v); }}
                data-testid={`post-react-${p.id}`} title="React"
                className={`inline-flex items-center gap-1.5 transition-colors ${p.my_reaction ? "text-fuchsia-700 font-700" : "text-muted-foreground hover:text-fuchsia-700"}`}>
                {myReact ? <span className="notranslate">{myReact.e} {myReact.l}</span> : <><ThumbsUp className="w-4 h-4" /> Like</>}
              </button>
              {showPicker && (
                <div className="absolute bottom-full left-0 mb-1.5 flex gap-1 bg-white border border-zinc-200 shadow-lg px-2 py-1.5 z-20 rounded-full" data-testid={`post-react-picker-${p.id}`}>
                  {REACTIONS.map(([v, e, l]) => (
                    <button key={v} title={l} onClick={() => { onReact(p.id, v); setShowPicker(false); }}
                      data-testid={`post-react-${v}-${p.id}`}
                      className={`text-lg leading-none hover:scale-125 transition-transform notranslate ${p.my_reaction === v ? "scale-110 drop-shadow" : ""}`}>{e}</button>
                  ))}
                </div>
              )}
            </div>
            {p.reaction_total > 0 && (
              <span className="text-xs text-muted-foreground notranslate" data-testid={`post-react-total-${p.id}`}>
                {topReacts.map(([k]) => REACT_META[k]?.e).join(" ")} {p.reaction_total}
              </span>
            )}
            <button onClick={() => setOpen((o) => !o)} data-testid={`post-comments-toggle-${p.id}`} className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-primary">
              <MessageCircle className="w-4 h-4" /> {p.comments?.length || 0}
            </button>
            <button onClick={() => setShowShare((v) => !v)} data-testid={`post-share-${p.id}`} title="Share" className={`inline-flex items-center gap-1.5 ${showShare ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
              <Share2 className="w-4 h-4" />
            </button>
            <button onClick={() => onSave(p.id)} data-testid={`post-save-${p.id}`} title="Save"
              className={`inline-flex items-center gap-1.5 ${p.saved_by_me ? "text-amber-600" : "text-muted-foreground hover:text-amber-600"}`}>
              <Bookmark className={`w-4 h-4 ${p.saved_by_me ? "fill-amber-500" : ""}`} />
            </button>
            <button onClick={() => onReport(p.id)} data-testid={`post-report-${p.id}`} title="Report" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-destructive">
              <Flag className="w-4 h-4" />
            </button>
          </div>

          {showShare && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`post-share-menu-${p.id}`}>
              <a href={`https://wa.me/?text=${encodeURIComponent(`${shareTitle} ${permalink}`)}`} target="_blank" rel="noreferrer"
                data-testid={`post-share-whatsapp-${p.id}`} className="border border-emerald-300 text-emerald-700 px-2 py-1 text-xs font-700 hover:bg-emerald-50">WhatsApp</a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(permalink)}`} target="_blank" rel="noreferrer"
                data-testid={`post-share-facebook-${p.id}`} className="border border-blue-300 text-blue-700 px-2 py-1 text-xs font-700 hover:bg-blue-50">Facebook</a>
              <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(permalink)}&text=${encodeURIComponent(shareTitle)}`} target="_blank" rel="noreferrer"
                data-testid={`post-share-x-${p.id}`} className="border border-zinc-400 text-zinc-800 px-2 py-1 text-xs font-700 hover:bg-zinc-100">X</a>
              <button onClick={copyLink} data-testid={`post-share-copy-${p.id}`}
                className="border border-indigo-300 text-indigo-700 px-2 py-1 text-xs font-700 hover:bg-indigo-50 inline-flex items-center gap-1"><Link2 className="w-3 h-3" /> Copy link</button>
              <Link to={`/community/post/${p.id}`} data-testid={`post-permalink-${p.id}`}
                className="text-xs text-muted-foreground hover:text-primary hover:underline">Open post page →</Link>
            </div>
          )}

          {(open || p.comments?.length > 0) && (
            <div className="mt-3 pl-3 border-l-2 border-zinc-200 space-y-3">
              {roots.map((c) => (
                <div key={c.id} className="space-y-2">
                  <CommentRow c={c} isChild={false} />
                  {(children[c.id] || []).map((cc) => <CommentRow key={cc.id} c={cc} isChild />)}
                </div>
              ))}
              {user && (
                <div>
                  {replyTo && (
                    <div className="text-[11px] text-fuchsia-700 font-700 mb-1 flex items-center gap-1.5" data-testid={`replying-to-${p.id}`}>
                      <CornerDownRight className="w-3 h-3" /> Replying to {roots.find((c) => c.id === replyTo)?.user_name || "comment"}
                      <button onClick={() => setReplyTo(null)} className="text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()}
                      data-testid={`comment-input-${p.id}`} placeholder={replyTo ? "Write your reply…" : "Write a reply…"}
                      className="flex-1 border border-zinc-300 px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary" />
                    <button onClick={addComment} data-testid={`comment-submit-${p.id}`} className="p-2 bg-primary text-white hover:bg-blue-700"><Send className="w-4 h-4" /></button>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <LangSelect compact value={replyLang} onChange={(v) => { setReplyLang(v); localStorage.setItem("tp_write_lang", v); }} testId={`comment-lang-${p.id}`} />
                    {canOfficial && (
                      <label className="inline-flex items-center gap-1.5 text-xs text-sky-700 cursor-pointer" data-testid={`comment-official-${p.id}`}>
                        <input type="checkbox" checked={replyOfficial} onChange={(e) => setReplyOfficial(e.target.checked)} />
                        <ShieldCheck className="w-3.5 h-3.5" /> Reply as official
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { PostCard };
